<template>
  <div class="relative" v-if="isLoggedIn" data-testid="header-notifications">
    <ZtPopup placement="bottom-end" @open:popup="onOpen" @close:popup="onClose">
      <div>
        <div
          v-if="hasUnread"
          class="absolute z-10 w-2 h-2 bg-red-500 border rounded-full pointer-events-none top-2 right-2 border-slate-200 dark:border-zinc-700"
          data-testid="header-notifications-unread-indicator"
        >
          <span class="sr-only">You have new notifications!</span>
        </div>
        <Button
          @click.prevent
          :type="hasUnread ? 'solid' : 'link'"
          :variant="hasUnread ? 'gray' : 'black'"
          icon="general.bell"
          data-testid="viewer-header-notifications-button"
          :aria-label="hasUnread ? 'Notifications' : undefined"
        />
      </div>

      <template #content>
        <section
          aria-describedby="titleNotifications"
          class="p-4 bg-white border shadow-xl dark:bg-zinc-800 top-full rounded-xl border-slate-50 dark:border-zinc-900 max-h-[32rem] and overflow-y-auto"
          ref="el"
        >
          <h2 class="sr-only" id="titleNotifications">Notifications</h2>
          <ul class="space-y-2">
            <li v-if="!items.length" class="flex items-center justify-between gap-4 p-4 bg-white rounded-10 dark:bg-zinc-800 min-w-max">
              <p class="dark:text-zinc-300">There are no notifications, yet.</p>
            </li>
            <NotificationsItem v-for="item in items" :key="item.id" :item="item" @clicked="markAsRead" />
            <li class="flex justify-center" v-if="isLoading">
              <Icon name="general.spinner" size="base" />
            </li>
          </ul>
        </section>
      </template>
    </ZtPopup>
  </div>
</template>
<script lang="ts" setup>
import { Button, Icon, ZtPopup } from '@/storybook';
import { useUserStore } from '@/store/useUserStore';
import { storeToRefs } from 'pinia';

import { useNotificationsStore } from '@/store/useNotificationsStore';
import { inject, onBeforeMount, ref, Ref, onUnmounted } from 'vue';
import NotificationsItem from './NotificationsItem.vue';
import { useInfiniteScroll } from '@vueuse/core';
import { PusherPlugin } from '@/plugins/pusher';

const LIMIT = 10;

const el = ref<HTMLElement | null>(null);
const isLoading = ref<boolean>(false);
const currentPage: Ref<number> = ref(1);

const userStore = useUserStore();
const notificationStore = useNotificationsStore();
const { isLoggedIn } = storeToRefs(userStore);
const { items, isLoadedAll, hasUnread } = storeToRefs(notificationStore);

const pusher = inject<PusherPlugin>('$pusher');

useInfiniteScroll(
  el,
  () => {
    if (isLoading.value || isLoadedAll.value) {
      return;
    }

    currentPage.value++;
    isLoading.value = true;

    notificationStore
      .loadMore(currentPage.value, LIMIT)
      .then(() => {
        isLoading.value = false;
      })
      .catch(() => {
        currentPage.value--;
        isLoading.value = false;
      });
  },
  { distance: LIMIT }
);

const onOpen = () => {
  notificationStore.load();
};

const markAsRead = (id: string) => notificationStore.markAsRead(id);
const onClose = () => notificationStore.markAllAsRead();

onBeforeMount(() => {
  notificationStore.load();
  if (pusher) {
    pusher.bindUser('notification', notificationStore.load);
  }
});

onUnmounted(() => {
  if (pusher) {
    pusher.unbindUser('notification', notificationStore.load);
  }
});
</script>
